/* eslint-disable */

const { VITE_ZX_APP_KEY, VITE_ZX_TRACK_DOMAIN, VITE_ZX_SDK_URL } = process.env;

type ShareChannelType =
  | 'ChannelWechat'
  | 'ChannelTimeLine'
  | 'ChannelSina'
  | 'ChannelQQ'
  | 'ChannelMeeTime'
  | 'ChannelAlbum';

/** 页面浏览事件数据 */
interface PageViewData {
  event_code: string;
  current_page_name: string;
  current_page_id: string;
  current_channel_name?: string;
  article_name?: string;
  article_type?: string;
  article_author?: string;
  article_id?: string;
  originality?: string;
  current_create_date?: string;
  special_topic_name?: string;
  [key: string]: unknown;
}

/** 点击事件数据 */
interface ClickData {
  current_page_name: string;
  current_page_id: string;
  button_name?: string;
  article_name?: string;
  article_type?: string;
  article_author?: string;
  article_id?: string;
  originality?: string;
  current_create_date?: string;
  special_topic_name?: string;
  share_channel_name?: string;
  [key: string]: unknown;
}

/** 其他/结果事件数据 */
interface OtherData {
  current_page_name: string;
  current_page_id: string;
  button_name?: string;
  is_successful?: string;
  article_name?: string;
  article_type?: string;
  article_author?: string;
  article_id?: string;
  originality?: string;
  current_create_date?: string;
  special_topic_name?: string;
  share_channel_name?: string;
  [key: string]: unknown;
}

/**
 * 中宣部 H5 埋点封装（基于 QuickTracking Web SDK）
 *
 * 说明：
 * - 在 App WebView 内打开的 H5 页面，由 App 端自行埋点，H5 不再重复上报
 * - 微信分享、外部浏览器等场景打开的 H5 页面，由 H5 自己走本 SDK 上报
 */
class ZhongXuan {
  /** SDK 是否已真正初始化完成（aplusReady complete） */
  static #isInit = false;
  /** SDK 是否已开始加载 */
  static #loading = false;
  /** SDK 就绪前暂存的事件，就绪后统一重放，避免时序竞争导致 sendPV 丢失 */
  static #pendingEvents: { action: string; args: unknown[] }[] = [];

  /**
   * 判断当前是否在 App WebView 内
   * 通过 UA 中的 App 标识判断，具体标识需与 App 端对齐
   */
  static #isInAppWebView() {
    const ua = navigator.userAgent;
    // 项目现有 App 标识，如 App 端有其他标识可在此补充
    return ua.indexOf('pdmiryun') > -1 || ua.indexOf('rmcb') > -1;
  }

  /**
   * 判断 H5 是否需要自己埋点
   */
  static #shouldTrackByH5() {
    return !this.#isInAppWebView();
  }

  static #loadScript(
    url: string,
    id: string,
    loadCb?: () => void,
    errorCb?: (err?: Event | string) => void,
  ) {
    const existScript = document.querySelector(`script[id="${id}"]`);
    if (existScript) {
      loadCb?.();
      return;
    }

    const firstScript = document.getElementsByTagName('script')[0];
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.id = id;
    script.src = url;
    script.onload = () => {
      loadCb?.();
    };
    script.onerror = (err) => {
      errorCb?.(err);
    };

    if (firstScript?.parentNode) {
      firstScript.parentNode.insertBefore(script, firstScript);
    } else {
      document.head.appendChild(script);
    }
  }

  /**
   * 初始化
   */
  static init() {
    // if (process.env.NODE_ENV === 'development') return Promise.resolve(false);
    if (!this.#shouldTrackByH5()) {
      return Promise.resolve(false);
    }
    if (this.#isInit) return Promise.resolve(true);

    if (!VITE_ZX_SDK_URL || !VITE_ZX_APP_KEY) {
      console.warn('[ZhongXuan] SDK 地址或 AppKey 未配置');
      return Promise.resolve(false);
    }

    return new Promise<boolean>((resolve, reject) => {
      // 先于脚本初始化队列，保证 meta 信息能被 SDK 消费到
      window.qt_queue = window.qt_queue || [];

      // 订阅 SDK 初始化完成事件
      window.qt_queue.push({
        action: 'aplus.aplus_pubsub.subscribe',
        arguments: [
          'aplusReady',
          (status: string) => {
            if (status === 'complete') {
              this.#isInit = true;
              this.#flushPendingEvents();
              resolve(true);
            }
          },
        ],
      });

      window.qt_queue.push({
        action: 'aplus.setMetaInfo',
        arguments: ['appKey', VITE_ZX_APP_KEY],
      });

      if (VITE_ZX_TRACK_DOMAIN) {
        window.qt_queue.push({
          action: 'aplus.setMetaInfo',
          arguments: ['trackDomain', VITE_ZX_TRACK_DOMAIN],
        });
      }

      // 全局关闭自动 PV：所有页面只走手动 sendPV，避免自动 PV 与手动 sendPV 重复上报（两条）。
      window.qt_queue.push({
        action: 'aplus.setMetaInfo',
        arguments: ['aplus-disable-apv', true],
      });

      // 补充通用属性（globalproperty），避免 SDK 消费队列时读取不到
      // 必要元信息而抛出 "Cannot read properties of undefined (reading 'O')"
      // 注意：globalproperty 在 quicktracking-sdk + 友盟老版 aplus 共存环境下可能触发 TypeError('O')，
      // 导致 SDK 内部状态错乱、aplusReady 永远不触发。这里去掉该 setMetaInfo，手动 sendPV 不依赖它。
      // window.qt_queue.push({
      //   action: 'aplus.setMetaInfo',
      //   arguments: [
      //     'globalproperty',
      //     {
      //       isLogin: 'false',
      //       user_id: '',
      //       channel_code: 'h5',
      //       custom_param: {
      //         source: 'h5',
      //       },
      //     },
      //   ],
      // });

      // 页面配置（pageConfig）：经实测，设置 pageConfig（含 skipMe）会导致手动 sendPV 上报失效，
      // 故完全移除 pageConfig，让手动 sendPV 走最原始方式上报，确保中宣埋点成功。
      // 副作用：中宣后台的「页面编码」会显示 URL 而非 article_details_pg（可接受，优先保证上报成功）。

      // 防重复加载 SDK
      if (this.#loading) return;
      this.#loading = true;

      this.#loadScript(
        VITE_ZX_SDK_URL,
        'beacon-aplus-zhongxuan',
        () => {
          // 关键：SDK 加载完成后，重新强制设置 appKey 和 trackDomain，
          // 覆盖友盟老版 aplus 可能对全局配置的污染（它默认会用 miniu.gzhuipu.com 收数）。
          // 必须在 SDK 接管 qt_queue 之后再 push，才能确保生效。
          if (window.qt_queue) {
            window.qt_queue.push({
              action: 'aplus.setMetaInfo',
              arguments: ['appKey', VITE_ZX_APP_KEY],
            });
            if (VITE_ZX_TRACK_DOMAIN) {
              window.qt_queue.push({
                action: 'aplus.setMetaInfo',
                arguments: ['trackDomain', VITE_ZX_TRACK_DOMAIN],
              });
            }
          }
        },
        (err) => {
          console.error('[ZhongXuan] 脚本加载失败:', err);
          reject(err);
        },
      );

      // 兜底：SDK 异常慢时，延后再尝试 resolve（默认 1.5 秒，避免用户感知延迟）。
      // 修复：之前版本 5s 后 resolve(false) 但 #isInit 仍是 false，
      // 导致 pendingEvents 永远不被 flush（只有 aplusReady complete 才会 flush），中宣埋点丢失。
      // 现在 1.5s 后如果 SDK 脚本已加载（#loading=true）但 #isInit=false，
      // 强制 #isInit=true 并 flush，让后续 push 能正常进入 qt_queue。
      setTimeout(() => {
        if (!this.#isInit) {
          if (this.#loading) {
            this.#isInit = true;
            this.#flushPendingEvents();
          }
          resolve(this.#isInit);
        }
      }, 1500);
    });
  }

  /** 将 SDK 就绪前暂存的事件重放给 qt_queue */
  static #flushPendingEvents() {
    if (!this.#pendingEvents.length) return;
    const pending = this.#pendingEvents.splice(0);
    for (const { action, args } of pending) {
      window.qt_queue?.push({ action, arguments: args });
    }
  }

  /**
   * 统一推入 qt_queue
   * SDK 未真正就绪（aplusReady complete）前，事件先暂存，就绪后重放，
   * 避免时序竞争导致 sendPV 在 SDK 初始化完成前被消费而抛错。
   */
  static #push(action: string, args: unknown[]) {
    // if (process.env.NODE_ENV === 'development') return;
    if (!this.#shouldTrackByH5()) {
      return;
    }

    if (!window.qt_queue) {
      this.#pendingEvents.push({ action, args });
      return;
    }

    // SDK 尚未初始化完成：先暂存，等 aplusReady 后统一重放
    if (!this.#isInit) {
      this.#pendingEvents.push({ action, args });
      return;
    }

    window.qt_queue.push({ action, arguments: args });
  }

  /**
   * 页面浏览事件
   */
  static pageView(data: PageViewData) {
    // 官方文档：sendPV 的 $properties 里可直接传 page_name / page_title 设置页面编码与标题，
    // 优先级高于 pageConfig。这样后台「页面编码」显示 event_code(article_details_pg)，而非 URL。
    this.#push('aplus.sendPV', [
      { is_auto: false },
      {
        ...data,
        event_type: 'pageview',
        page_name: data.event_code,
        page_title: data.current_page_name,
      },
    ]);
  }

  /**
   * 点击事件
   * @param eventCode 事件编码，如 article_details_clk
   */
  static click(eventCode: string, data: ClickData) {
    this.#push('aplus.record', [
      eventCode,
      'CLK',
      { ...data, event_type: 'click', event_code: eventCode },
    ]);
  }

  /**
   * 其他/结果事件
   * @param eventCode 事件编码，如 article_publish_result
   */
  static other(eventCode: string, data: OtherData) {
    this.#push('aplus.record', [
      eventCode,
      'OTHER',
      { ...data, event_type: 'other', event_code: eventCode },
    ]);
  }

  /**
   * 分享事件（基于其他事件包装）
   */
  static shareArticle(articleId: string, shareChannel: ShareChannelType, extra: Record<string, unknown> = {}) {
    this.other('article_share_result', {
      article_id: articleId,
      share_channel_name: shareChannel,
      ...extra,
    } as OtherData);
  }
}

export default ZhongXuan;
